import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateConverterService {

  // Adapted from https://github.com/angular/angular/blob/master/packages/common/src/i18n/format_date.ts

  /**
   * @license
   * Copyright Google LLC All Rights Reserved.
   *
   * Use of this source code is governed by an MIT-style license that can be
   * found in the LICENSE file at https://angular.io/license
   */

  private readonly ISO8601_DATE_PART = /^(\d{4})-?(\d\d)-?(\d\d)/;
  private readonly ISO8601_TIME_TZ_PART = /T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?$/;

  /**
   * Converts a value to date.
   *
   * Supported input formats:
   * - `Date`
   * - number: timestamp
   * - string: numeric (e.g. "1234"), ISO and date strings in a format supported by
   *   [Date.parse()](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse).
   *   Note: ISO strings without time return a date without timeoffset.
   *
   * Throws if unable to convert to a date.
   */
  public toDate(value: string | number | Date): Date {
    if (this.isDate(value)) {
      return value as Date;
    }

    if (typeof value === 'number' && !Number.isNaN(value)) {
      return new Date(value);
    }

    if (typeof value === 'string') {
      value = value.trim();

      const parsedNb = Number.parseFloat(value);

      // any string that only contains numbers, like "1234" but not like "1234hello"
      if (!Number.isNaN((value as unknown as number) - parsedNb)) {
        return new Date(parsedNb);
      }

      if (/^(\d{4}-\d{1,2}-\d{1,2})$/.test(value)) {
        /* For ISO Strings without time the day, month and year must be extracted from the ISO String
        before Date creation to avoid time offset and errors in the new Date.
        If we only replace '-' with ',' in the ISO String ("2015,01,01"), and try to create a new
        date, some browsers (e.g. IE 9) will throw an invalid Date error.
        If we leave the '-' ("2015-01-01") and try to create a new Date("2015-01-01") the timeoffset
        is applied.
        Note: ISO months are 0 for January, 1 for February, ... */
        const [y, m, d] = value.split('-').map((val: string) => +val);
        return new Date(y, m - 1, d);
      }

      const match = this.matchISO8601(value);
      if (match) {
        return this.isoStringToDate(match);
      }
    }

    const date = new Date(value as string | number);
    if (!this.isDate(date)) {
      throw new TypeError(`Unable to convert "${value}" into a date`);
    }
    return date;
  }

  /**
   * Converts a date in ISO8601 to a Date.
   * Used instead of `Date.parse` because of browser discrepancies.
   */
  private matchISO8601(value: string): RegExpMatchArray | null {
    const datePart = this.ISO8601_DATE_PART.exec(value);
    if (!datePart) return null;
    const rest = value.slice(datePart[0].length);
    if (rest && !rest.startsWith('T')) return null;
    const timePart = rest ? this.ISO8601_TIME_TZ_PART.exec(rest) : null;
    if (rest && !timePart) return null;
    return [value, datePart[1], datePart[2], datePart[3],
      timePart?.[1], timePart?.[2], timePart?.[3], timePart?.[4],
      timePart?.[5], timePart?.[6], timePart?.[7], timePart?.[8]
    ] as unknown as RegExpMatchArray;
  }

  private isoStringToDate(match: RegExpMatchArray): Date {
    const date = new Date(0);
    let tzHour = 0;
    let tzMin = 0;

    // match[8] means that the string contains "Z" (UTC) or a timezone like "+01:00" or "+0100"
    const dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear;
    const timeSetter = match[8] ? date.setUTCHours : date.setHours;

    // if there is a timezone defined like "+01:00" or "+0100"
    if (match[9]) {
      tzHour = Number(match[9] + match[10]);
      tzMin = Number(match[9] + match[11]);
    }
    dateSetter.call(date, Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    const h = Number(match[4] || 0) - tzHour;
    const m = Number(match[5] || 0) - tzMin;
    const s = Number(match[6] || 0);
    // The ECMAScript specification (https://www.ecma-international.org/ecma-262/5.1/#sec-15.9.1.11)
    // defines that `DateTime` milliseconds should always be rounded down, so that `999.9ms`
    // becomes `999ms`.
    const ms = Math.floor(Number.parseFloat('0.' + (match[7] || 0)) * 1000);
    timeSetter.call(date, h, m, s, ms);
    return date;
  }

  private isDate(value: unknown): boolean {
    return value instanceof Date && !Number.isNaN(value.valueOf());
  }
}
